import { useEffect, useMemo, useState } from "react";

import { CommonModels } from "@/data/common/common.models";
import type { ProfileModels } from "@/data/profile/profile.models";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { WorthDataQueries } from "@/data/worthData/worthData.queries";
import useInvalidateProfile from "@/hooks/profile/useInvalidateProfile";
import { ProfileUtils } from "@/util/profile.utils";

/**
 * Hook to fetch user profile and worth data without polling.
 * Worth data updates are handled via SSE events (useWorthCalcSse).
 * Profile polling is still enabled when needed (during onboarding/processing).
 */
const useUserProfileAndWorthData = () => {
  const { invalidateAndRefetchProfile } = useInvalidateProfile();

  const [isProfilePollingEnabled, setIsProfilePollingEnabled] = useState(true);
  const { data: userProfile } = ProfileQueries.useGetMy<ProfileModels.FullProfileResponseDto>({
    refetchInterval: 5000,
    enabled: isProfilePollingEnabled,
  });

  // Worth data query WITHOUT polling - updates come from SSE events
  const { data: worthData } = WorthDataQueries.useGet<CommonModels.WorthNumbersDto>({
    enabled: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (userProfile?.status === CommonModels.ProfileStatusEnum.processed) {
      invalidateAndRefetchProfile();
    }
    setIsProfilePollingEnabled(ProfileUtils.getProfilePollingEnabled(userProfile?.status || undefined));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.status]);

  return useMemo(
    () => ({
      userProfile,
      worthData,
    }),
    [userProfile, worthData],
  );
};

export default useUserProfileAndWorthData;
